// Die Startseite der App.
//
// Nach dem Entsperren stehen hier große Kacheln für alles Wichtige. Erst von
// hier geht es weiter. Das ist bewusst anders als am Schreibtisch, wo die
// Übersicht mit Zahlen beginnt und die Navigation links immer sichtbar ist:
// auf einem Telefon gibt es keine ständig sichtbare Navigation, also braucht
// es einen Ort, an dem alles sichtbar ist.
//
// Was der Tarif nicht enthält oder die Rolle nicht darf, erscheint gar nicht.
// Eine ausgegraute Kachel wäre eine Lüge — den Hinweis auf den nächsten Tarif
// gibt es auf der Zielseite über <TarifSperre>.
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../gemeinsam/lib/AuthContext';
import { useSprache } from '../gemeinsam/lib/i18n';
import type { Schluessel } from '../gemeinsam/lib/i18n';
import { useKennzahlen } from '../bereiche/start/lib/kennzahlen';
import { useBenachrichtigungen } from '../bereiche/aufgaben/lib/benachrichtigungen';
import { erlaubt, GRUPPEN, UEBERSICHT } from '../gemeinsam/lib/navigation';
import type { NavEintrag } from '../gemeinsam/lib/navigation';
import { euro } from '../gemeinsam/lib/daten';
import Icon from '../gemeinsam/komponenten/Icon';
import Seitenkopf, { KopfKnopf } from './Seitenkopf';
import Glocke from '../bereiche/aufgaben/komponenten/Glocke';
import { rolleVon } from './Tableiste';

type Kachel = { eintrag: NavEintrag; zahl?: string; punkt?: number };

const ALLE = [UEBERSICHT, ...GRUPPEN.flatMap(g => g.eintraege)];
const von = (pfad: string) => ALLE.find(e => e.pfad === pfad)!;

export default function Kachelbrett({ suchen }: { suchen?: () => void }) {
  const { benutzer, mandant, flags, istOwner, istAdmin } = useAuth();
  const { t, sprache } = useSprache();
  const { anzahlChat } = useBenachrichtigungen();
  const navigate = useNavigate();
  const k = useKennzahlen(istAdmin);

  const rolle = rolleVon(istOwner, istAdmin);
  const heute = new Date().toDateString();

  const umsatzHeute = k.rechnungen
    .filter(r => r.status !== 'entwurf' && new Date(r.createdAt).toDateString() === heute)
    .reduce((s, r) => s + r.brutto, 0);
  const offeneRechnungen = k.rechnungen.filter(r => r.status === 'gestellt');
  const offenerBetrag = offeneRechnungen.reduce((s, r) => s + r.brutto, 0);
  const lagerwert = k.artikel.reduce((s, p) => s + (p.bestand || 0) * (p.einkaufspreis || 0), 0);
  const knapp = k.artikel.filter(p => (p.bestand ?? 0) <= (p.mindestbestand ?? 0));
  const offeneAufgaben = k.aufgaben.filter(a => a.column !== 'done');
  const heutigeBelege = k.rechnungen.filter(r => new Date(r.createdAt).toDateString() === heute).length;

  // Ohne Netz bleiben die Zahlen leer statt falsch: eine „0,00 €" wäre eine
  // Aussage über den Betrieb, kein Hinweis auf eine fehlende Verbindung.
  const zahl = (text: string) => (k.fehler || k.laedt ? undefined : text);

  const gruppen: Array<{ titel: Schluessel; kacheln: Kachel[] }> = [
    {
      titel: 'app.gruppe.tagesgeschaeft',
      kacheln: [
        { eintrag: von('/kasse'),      zahl: zahl(euro(umsatzHeute)) },
        { eintrag: von('/lager'),      zahl: zahl(`${k.artikel.length} ${t('lager.anzahlArtikel')} · ${euro(lagerwert)}`), punkt: knapp.length },
        // Belege sind eine eigene Sammlung, die hier nicht geholt wird.
        // Lieber keine Zahl als die falsche.
        { eintrag: von('/belege') },
        { eintrag: von('/rechnungen'), zahl: zahl(offeneRechnungen.length ? euro(offenerBetrag) : euro(0)) },
      ],
    },
    {
      titel: 'app.gruppe.kontakte',
      kacheln: [{ eintrag: von('/kunden') }, { eintrag: von('/lieferanten') }],
    },
    {
      titel: 'app.gruppe.zusammenarbeit',
      kacheln: [
        { eintrag: von('/aufgaben'), zahl: zahl(String(offeneAufgaben.length)), punkt: offeneAufgaben.length },
        { eintrag: von('/chat'), punkt: anzahlChat },
        { eintrag: von('/assistent') },
        { eintrag: von('/support') },
      ],
    },
    {
      titel: 'app.gruppe.auswertung',
      kacheln: [{ eintrag: von('/buchhaltung') }, { eintrag: von('/bank') }],
    },
    {
      titel: 'app.gruppe.betrieb',
      kacheln: [{ eintrag: von('/owner') }],
    },
  ];

  const sichtbar = gruppen
    .map(g => ({ ...g, kacheln: g.kacheln.filter(x => erlaubt(x.eintrag, rolle, flags)) }))
    .filter(g => g.kacheln.length > 0);

  const vorname = (benutzer?.name ?? '').split(' ')[0];
  const datumText = new Intl.DateTimeFormat(sprache, {
    weekday: 'long', day: 'numeric', month: 'long',
  }).format(new Date());

  return (
    <>
      <Seitenkopf
        titel={t('app.name')}
        aktionen={
          <>
            <Glocke />
            {suchen && <KopfKnopf symbol="suche" beschriftung={t('aktion.suchen')} onClick={suchen} />}
          </>
        }
      />

      <div data-rumpf className="flex-1 overflow-y-auto pb-6">
        <div className="px-5 pb-1 pt-3">
          <h2 className="text-[27px] font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t('app.gruss').replace('{name}', vorname)}
          </h2>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            {mandant?.name} · {datumText}
          </p>
        </div>

        {k.fehler && (
          <p className="mx-4 mt-2 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            {t('app.keineVerbindung')}
          </p>
        )}

        {/* Zahlenstreifen: waagerecht, damit er auf 375 px nicht umbricht. */}
        <div className="flex gap-2.5 overflow-x-auto px-4 pt-3.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Zahlkarte was={t('uebersicht.umsatzHeute')} wert={zahl(euro(umsatzHeute)) ?? '—'}
                     dazu={`${heutigeBelege} ${t('nav.belege')}`} />
          <Zahlkarte was={t('uebersicht.offeneForderungen')} wert={zahl(euro(offenerBetrag)) ?? '—'}
                     dazu={`${offeneRechnungen.length} ${t(offeneRechnungen.length === 1 ? 'allg.rechnung' : 'nav.rechnungen')}`} ton="gelb" />
          <Zahlkarte was={t('uebersicht.lagerwert')} wert={zahl(euro(lagerwert)) ?? '—'}
                     dazu={`${k.artikel.length} ${t('lager.anzahlArtikel')}`} />
        </div>

        <div className="px-4 pb-2 pt-1">
          {sichtbar.map(g => (
            <section key={g.titel} className="mt-4">
              <h3 className="px-1 pb-2 text-[11.5px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {t(g.titel)}
              </h3>
              <div className="grid grid-cols-2 gap-2.5">
                {g.kacheln.map(x => (
                  <button
                    key={x.eintrag.pfad}
                    onClick={() => navigate(x.eintrag.pfad)}
                    className="relative flex aspect-[1/0.84] flex-col justify-between rounded-[20px] border border-slate-200 bg-white p-3.5 text-start shadow-sm transition active:scale-[.96] dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
                  >
                    {!!x.punkt && x.punkt > 0 && (
                      <span className="absolute end-3 top-3 grid h-[21px] min-w-[21px] place-items-center rounded-full bg-rose-500 px-1.5 text-[11.5px] font-bold text-white">
                        {x.punkt > 99 ? '99+' : x.punkt}
                      </span>
                    )}
                    <span className="grid size-11 place-items-center rounded-[14px] bg-marke-50 text-marke-600 dark:bg-marke-950/60 dark:text-marke-400">
                      <Icon name={x.eintrag.symbol} groesse={23} />
                    </span>
                    <span>
                      <span className="block text-[15px] font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                        {t(x.eintrag.schluessel)}
                      </span>
                      {x.zahl && (
                        <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                          {x.zahl}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  );
}

function Zahlkarte({ was, wert, dazu, ton }: {
  was: string; wert: string; dazu: string; ton?: 'gelb';
}) {
  return (
    <div className="w-[138px] shrink-0 rounded-[17px] border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-[11.5px] font-medium text-slate-500 dark:text-slate-400">{was}</p>
      <p className={`mt-0.5 text-xl font-bold tracking-tight ${
        ton === 'gelb' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-slate-100'}`}>
        {wert}
      </p>
      <p className="mt-0.5 truncate text-[11.5px] text-slate-400 dark:text-slate-500">{dazu}</p>
    </div>
  );
}
